import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { EmailThreatsByTypeResponse } from 'src/app/core/models/analytics/es-models';

const GROUP_COLORS: Record<string, string> = {
  Phishing: '#0d6efd',
  Spam: '#52695f',
  Malware: '#e0a03a',
  Graymail: '#8a9a5b',
  DLP: '#6d5bd0',
  'Shadow IT': '#1fa2a6',
  Anomaly: '#d9534f',
  Other: '#b8c0cc',
};

// Donut ring: r=60 in a 180x180 viewBox, with a small gap between slices.
const CIRCUMFERENCE = 2 * Math.PI * 60;
const SLICE_GAP = 2;

@Component({
  selector: 'app-es-sec-events-breakdown',
  standalone: true,
  imports: [DecimalPipe],
  templateUrl: './es-sec-events-breakdown.component.html',
  styleUrl: './es-sec-events-breakdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsSecEventsBreakdownComponent {
  readonly circumference = CIRCUMFERENCE;

  emailThreatsByType = input<EmailThreatsByTypeResponse>();

  total = computed(() => this.emailThreatsByType()?.total_security_events ?? 0);

  // Biggest group first, each with its share, its color and where its slice sits on the ring.
  events = computed(() => {
    const total = this.total();
    let offset = 0;
    return [...(this.emailThreatsByType()?.security_events ?? [])]
      .sort((a, b) => b.count - a.count)
      .map((event) => {
        const length = total ? (event.count / total) * CIRCUMFERENCE : 0;
        const slice = {
          ...event,
          pct: total ? Math.round((event.count / total) * 100) : 0,
          color: GROUP_COLORS[event.value] ?? GROUP_COLORS['Other'],
          dash: Math.max(length - SLICE_GAP, 0),
          offset: -offset,
        };
        offset += length;
        return slice;
      });
  });

  ariaLabel = computed(
    () => 'Donut chart: ' + this.events().map((e) => `${e.value} ${e.pct}%`).join(', ')
  );
}
